<template>
  <teleport to="body">
    <!-- Popover 气泡弹出框 -->
    <transition name="popover">
      <div 
        v-if="show && popoverStyle" 
        class="qrcode-popover"
        :style="popoverStyle"
        @click.stop
      >
        <!-- 小箭头 -->
        <div class="popover-arrow" :style="arrowStyle"></div>
        
        <!-- 内容区域 -->
        <div class="popover-content" @click.stop>
          <!-- <div class="popover-header" @click.stop>
            <h3 class="qrcode-title">{{ title }}</h3>
            <button class="qrcode-close-btn" @click.stop="handleClose">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div> -->
          <div class="popover-body" @click.stop>
            <img 
              :src="$fixAssetUrl(qrcodeImage)" 
              :alt="title"
              class="qrcode-image"
              @error="handleImageError"
              @touchstart="handleTouchStart"
              @touchend="handleTouchEnd"
              @click.stop="handleImageClick"
            />
            <p class="qrcode-hint">长按图片可保存</p>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- 点击外部关闭的遮罩层 -->
    <div 
      v-if="show" 
      class="popover-backdrop"
      @click="handleOverlayClick"
    ></div>
  </teleport>
</template>

<script setup>
import { watch, ref, computed, nextTick } from 'vue'

/**
 * 二维码 Popover 组件
 * @property {Boolean} show - 是否显示弹窗
 * @property {String} qrcodeImage - 二维码图片路径
 * @property {String} title - 弹窗标题
 * @property {Boolean} closeOnClickOverlay - 点击遮罩层是否关闭，默认 true
 * @property {HTMLElement} triggerElement - 触发元素，用于定位 Popover
 */
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  qrcodeImage: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: '扫码关注'
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: true
  },
  triggerElement: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:show', 'close', 'image-long-press'])

/**
 * Popover 位置样式
 */
const popoverStyle = ref(null)
const arrowStyle = ref(null)

/**
 * 长按相关状态
 */
const touchStartTime = ref(0)
const longPressTimer = ref(null)
const LONG_PRESS_DURATION = 500 // 长按时间阈值（毫秒）

/**
 * 计算 Popover 位置
 */
const calculatePosition = async () => {
  if (typeof window === 'undefined' || !props.triggerElement) {
    return
  }
  
  await nextTick()
  
  const trigger = props.triggerElement
  const rect = trigger.getBoundingClientRect()
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  
  // Popover 尺寸（估算）
  const popoverWidth = 280
  const popoverHeight = 280 // 调整为更准确的高度，原为 320
  const arrowSize = 8
  
  // 触发元素中心位置（用于计算箭头位置）
  const triggerCenterX = rect.left + (rect.width / 2)
  
  // 计算位置：显示在触发元素上方
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  
  // 直接让弹窗在视口内水平居中
  // 公式：(视口宽度 - 弹窗宽度) / 2
  const finalLeft = (viewportWidth - popoverWidth) / 2
  
  const top = rect.top - popoverHeight - arrowSize - 10 
  
  // 允许弹窗位置更靠下，增加底部边界
  const finalTop = Math.max(3, Math.min(top, viewportHeight - popoverHeight + 20))
  
  // 箭头位置：始终指向触发元素中心
  // 无论popover的left值如何变化，箭头都要指向触发元素中心
  // 箭头本身宽度为16px（border-left: 8px + border-right: 8px），需要减去一半（8px）使箭头中心对准触发元素中心
  const arrowLeft = triggerCenterX - finalLeft - 8
  
  popoverStyle.value = {
    left: `${finalLeft}px`,
    top: `${finalTop}px`
  }
  
  arrowStyle.value = {
    left: `${arrowLeft}px`
  }
}

/**
 * 关闭弹窗
 */
const handleClose = () => {
  emit('update:show', false)
  emit('close')
}

/**
 * 点击遮罩层
 */
const handleOverlayClick = () => {
  if (props.closeOnClickOverlay) {
    handleClose()
  }
}

/**
 * 图片加载错误处理
 */
const handleImageError = (e) => {
  console.error('QR code image load error:', e.target.src)
}

/**
 * 触摸开始
 */
const handleTouchStart = (e) => {
  touchStartTime.value = Date.now()
  longPressTimer.value = setTimeout(() => {
    // 触发长按事件
    emit('image-long-press', props.qrcodeImage, props.title)
    // 触觉反馈（如果支持）
    if (navigator.vibrate) {
      navigator.vibrate(50)
    }
  }, LONG_PRESS_DURATION)
}

/**
 * 触摸结束
 */
const handleTouchEnd = () => {
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
}

/**
 * 点击图片（用于放大预览）
 */
const handleImageClick = () => {
  // 点击图片也触发放大预览
  emit('image-long-press', props.qrcodeImage, props.title)
}

/**
 * 监听 show 变化，计算位置和控制 body 滚动
 */
watch(() => props.show, async (newVal) => {
  if (typeof window === 'undefined' || !document) {
    return
  }
  
  if (newVal) {
    document.body.style.overflow = 'hidden'
    await calculatePosition()
  } else {
    document.body.style.overflow = ''
    popoverStyle.value = null
    arrowStyle.value = null
  }
}, { immediate: true })

/**
 * 监听触发元素变化，重新计算位置
 */
watch(() => props.triggerElement, () => {
  if (props.show) {
    calculatePosition()
  }
})
</script>

<style scoped lang="scss">
.popover-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1999;
  background: transparent;
}

.qrcode-popover {
  position: fixed;
  z-index: 2000;
  pointer-events: auto;
  
  .popover-arrow {
    position: absolute;
    bottom: -8px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #fff;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
  
  .popover-content {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    min-width: 260px;
    max-width: 320px;
    
    .popover-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      
      .qrcode-title {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin: 0;
      }
      
      .qrcode-close-btn {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        cursor: pointer;
        color: #999;
        padding: 0;
        transition: color 0.2s;
        flex-shrink: 0;
        
        &:hover {
          color: #333;
        }
        
        &:active {
          opacity: 0.7;
        }
        
        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
    
    .popover-body {
      padding: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      
      .qrcode-image {
        width: 100%;
        max-width: 220px;
        height: auto;
        display: block;
        border-radius: 8px;
        user-select: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;
        touch-action: manipulation;
      }
      
      .qrcode-hint {
        margin-top: 12px;
        font-size: 12px;
        color: #999;
        text-align: center;
        margin-bottom: 0;
      }
    }
  }
}

// Popover 弹出动画
.popover-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.popover-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.popover-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.popover-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>
